/**
 * Adapter between the server's room messages and the game's own surfaces.
 * Isolating it keeps `Game` free of transport vocabulary.
 */

import type { AuthorityFrame } from "../shared/action-protocol"
import type { RaceResult, RoomPhase, RoomSnapshot } from "../shared/room"
import type { RoomSession } from "../shared/session"
import type { PlayerId } from "../shared/types"
import type { PartyEvents } from "./party-link"

export type PartyPorts = {
  connection(reconnecting: boolean, rttMs?: number): void
  identify(you: PlayerId, snapshot: RoomSnapshot, seed: number): void
  roster(snapshot: RoomSnapshot): void
  phase(
    phase: RoomPhase,
    phaseEndsAtMs: number | null,
    serverNowMs: number,
    session?: RoomSession,
  ): void
  results(results: readonly RaceResult[], session?: RoomSession): void
  failure(message: string): void
  closed(reason: string): void
  frame(frame: AuthorityFrame): void
  pong(nonce: number, serverNowMs: number): void
}

export const partyEvents = (ports: PartyPorts): PartyEvents => ({
  onConnection: (reconnecting, rttMs) => ports.connection(reconnecting, rttMs),
  onWelcome: (you, snapshot, seed) => ports.identify(you, snapshot, seed),
  onRoster: (snapshot) => ports.roster(snapshot),
  onPhase: (phase, endsAtMs, serverNowMs, session) =>
    ports.phase(phase, endsAtMs, serverNowMs, session),
  onResults: (results, session) => ports.results(results, session),
  onFailure: (message) => ports.failure(message),
  onClosed: (reason) => ports.closed(reason),
  onFrame: (frame) => ports.frame(frame),
  onPong: (nonce, serverNowMs) => ports.pong(nonce, serverNowMs),
})
